import { EContractTab, ERowStatus } from "api/enums";
import ContractRowMovimentService from "api/services/ContractRowMovimentService";
import ContractService from "api/services/ContractService";
import { makeAutoObservable } from "mobx";
import { ContractStore, UserStore } from "..";
import ContractRowModel from "../../api/models/ContractRowModel";
import WBSModel from "../../api/models/WBSModel";
import ContractRowService from "../../api/services/ContractRowService";
import { formatNumber, formatNumberToMonetary } from "../../utils";
import IdleTimer from "../../utils/IdleTimer";

export type TRowData = {
  code: string;
  description: string;
  quantity?: string;
  unit?: string;
  unitCost?: string;
  total?: string;
  perc: string;
  visible: boolean;
  isStage: boolean;
  level: number;
  status: string;
  expanded?: boolean;
  selected?: boolean;
  customClass?: string;
  cellCustoms?: any;
};

class Store {
  private _rowsList: WBSModel<ContractRowModel> | undefined;
  private _rowCount: number = 0;
  private _allRowsLoaded: boolean = false;

  private _timer: any;
  private _timerIdle: any;
  private _canFetchRows: boolean = true;
  private _forceFetchRows: boolean = false;
  private _selectedRows: { index: number; row: ContractRowModel }[] = [];
  public tableLoading: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  reset() {
    this._rowsList = undefined;
    this._rowCount = 0;
    this._allRowsLoaded = false;
    this._canFetchRows = true;
    this._forceFetchRows = false;
    this._selectedRows = [];
    this.stopTimer();
  }

  get rowsCount(): number {
    return this._rowCount;
  }

  getAllRows() {
    return this._rowsList?.getAllRows();
  }

  get allRowsLoaded() {
    return this._allRowsLoaded;
  }

  subscribeContractRows() {
    this.reset();
    const contractUid = ContractStore.getCurrentContract()?.uid;
    if (!contractUid) return;

    this._rowsList = new WBSModel<ContractRowModel>(contractUid, 1000);
    this.getContractRowsByBatch();

    this.startTimer(() => {
      if (this._canFetchRows) this.getContractRowsByBatch();
    });
  }

  unsubscribeContractRows() {
    ContractRowService.unsubscribeRows();
    this.reset();
  }

  forceFetchAllContractRows() {
    this._forceFetchRows = true;
  }

  private getContractRowsByBatch() {
    if (this.allRowsLoaded) return;

    const contractUid = ContractStore.getCurrentContract()?.uid;
    if (!this._rowsList || !contractUid) return;
    this.setCanFecthRows(false);

    try {
      const onCreate = (rows) => {
        this.addRowsToList(rows);
        this.updateRowsCount();
        this.setCanFecthRows(true);
      };

      const onFinish = () => {
        this.stopTimer();
        this.setAllRowsLoaded(true);
      };

      ContractRowService.subscribeContractsRows(
        contractUid,
        this._rowsList,
        onCreate,
        undefined,
        onFinish
      );
    } catch (error) {
      console.error(error);
      this.setCanFecthRows(true);
    }
  }

  private addRowsToList(rows) {
    if (this._rowsList && rows?.length) this._rowsList.addRowList(rows);
  }

  getBlanckRow(index): TRowData {
    return {
      code: String(index + 1),
      description: " ",
      quantity: "0",
      unit: "",
      unitCost: "0,00",
      total: "0,00",
      perc: "0",
      visible: true,
      expanded: false,
      isStage: false,
      level: 1,
      status: "",
    };
  }

  getRowFormated(index): TRowData | undefined {
    const contractTotal = ContractStore.currentContractTotal;
    const row = this.getRowByIndex(index);
    if (!row) return;

    const stage = row.isStage;
    return {
      ...row.customProperties,
      code: row.structured,
      description: row.description,
      quantity: stage ? "" : formatNumber(row.quantity, 2, 5),
      unit: row.unit,
      unitCost: stage ? "" : formatNumberToMonetary(row.unitCost, false),
      total: formatNumberToMonetary(row.total, false),
      perc: formatNumber(row.getPercentual(contractTotal)),
      visible: row.visible,
      expanded: row.expanded,
      selected: row.selected,
      isStage: stage,
      level: row.level,
      status: row.status,
    };
  }

  toggleChildrenRow(index: number, parentCode: string) {
    const rowParent = this.getRowByIndex(index);
    if (!rowParent) return;

    rowParent.expanded = !rowParent.expanded;

    const visible = !rowParent.expanded;
    const children = this.getChildrenByCode(parentCode);
    children.forEach((row) => {
      row.visible = visible;
      row.expanded = false;
    });

    this.updateRowsCount();
  }

  getSelectedRow(): ContractRowModel | undefined {
    if (this._selectedRows.length) {
      const [item] = this._selectedRows;
      return item.row;
    }
    return;
  }

  getSelectedRowIndex(): number {
    if (this._selectedRows.length) {
      const [item] = this._selectedRows;
      return item.index;
    }
    return -1;
  }

  selectRowByCode(code: string) {
    const row = this.getRowByCode(code);
    this.selectRow(row);
  }

  selectRowByIndex(index: number) {
    const row = this.getRowByIndex(index);
    this.selectRow(row);
  }

  private selectRow(row?: ContractRowModel) {
    if (
      !row ||
      !this._rowsList ||
      (row.isStage && row.status === ERowStatus.UNCHAGED)
    )
      return;

    this.unselectRows();
    row.selected = true;

    if (!this._selectedRows.find((r) => r.row.uid === row.uid)) {
      const index = this._rowsList.findVisibleRowIndexByCode(
        row.structured,
        this.getOpenedPage()
      );
      this._selectedRows.push({ index, row });
    }
  }

  unselectRows() {
    this._selectedRows.forEach((item) => {
      item.row.selected = false;
    });
    this._selectedRows = [];
  }

  setTablesLoading(value: boolean) {
    this.tableLoading = value;
  }

  getRowByIndex(index: number): ContractRowModel | undefined {
    if (!this._rowsList) return;
    return this._rowsList.getRowVisibleByIndex(index, this.getOpenedPage());
  }

  getRowByCode(code: string): ContractRowModel | undefined {
    if (!this._rowsList) return;
    return this._rowsList.getRowByCode(code);
  }

  getAllStructureByCode(code: string): ContractRowModel[] {
    if (!this._rowsList) return [];
    return this._rowsList.getStrutureByCode(code);
  }

  getChildrenByCode(code: string): ContractRowModel[] {
    if (!this._rowsList) return [];
    return this._rowsList.getChildrenByCode(code);
  }

  getFirstLevelStages(): ContractRowModel[] {
    if (!this._rowsList) return [];
    return this._rowsList.getChildrenByUid("-1");
  }

  findParentsByCode(code: string): ContractRowModel[] {
    if (!this._rowsList) return [];
    return this._rowsList.getParentsByCode(code);
  }

  countSiblingsByParentUid(parentUid: string): number {
    if (!this._rowsList) return 0;
    return this._rowsList.getChildrenByUid(parentUid).length;
  }

  refreshContractRows() {
    this.reset();
    this.subscribeContractRows();
  }

  public async updateRowDescription(code: string, updatedDescription: string) {
    const authenticatedUser = UserStore.currentUser;
    if (!authenticatedUser) return;

    const contract = ContractStore.getCurrentContract();
    if (!contract) return;

    const row = this.getRowByCode(code);
    if (!row) return;

    const oldDescription = row.description;
    await ContractRowService.updateRowDescription(row, updatedDescription);
    await ContractRowMovimentService.createDescriptionRowMoviment(
      row,
      oldDescription,
      authenticatedUser
    );

    if (!contract.hasChanged) {
      await ContractService.markContractAsChanged(contract);
    }
  }

  private updateRowsCount() {
    const count = this._rowsList?.countVisibleRows(this.getOpenedPage()) || 0;
    this._rowCount = count;
  }

  private setAllRowsLoaded(value: boolean) {
    if (value !== this._allRowsLoaded) this._allRowsLoaded = value;
  }

  private setCanFecthRows(value: boolean) {
    this._canFetchRows = value;
  }

  private startTimer(callback) {
    this._timer = setInterval(() => {
      if (this.userIsIdle() || this._forceFetchRows) callback();
    }, 1000);

    this._timerIdle = new IdleTimer({ timeout: 5 });
    this._timerIdle.start();
  }

  private stopTimer() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }

    if (this._timerIdle) this._timerIdle.stop();
  }

  private userIsIdle() {
    return this._timerIdle.status !== "active";
  }

  private getOpenedPage() {
    return ContractStore.currentTabOpened === EContractTab.CONTRACT
      ? "contract"
      : "measurement";
  }
}

const ContractRowStore = new Store();
export default ContractRowStore;
